import moment from "moment";
import { PickupStatsTimeSeriesPoint } from "../../../../app/data/pickup-requests/models";
import { getTimeSeriesDate } from "./pickupStatsFormatters";

export type PickupTimeSeriesViewMode = "daily" | "weekly" | "monthly";

interface GroupedTimeSeriesPoint {
  label: string;
  sortKey: number;
  received: number;
  autoCreated: number;
  approvedByHuman: number;
  rejectedByHuman: number;
}

const VOLUME_DATASET_STYLES = [
  {
    label: "Received",
    key: "received" as const,
    borderColor: "#4285F4",
    backgroundColor: "rgba(66, 133, 244, 0.15)",
  },
  {
    label: "Auto-created",
    key: "autoCreated" as const,
    borderColor: "#0F9D58",
    backgroundColor: "rgba(15, 157, 88, 0.15)",
  },
  {
    label: "Employee approved",
    key: "approvedByHuman" as const,
    borderColor: "#A142F4",
    backgroundColor: "rgba(161, 66, 244, 0.15)",
  },
  {
    label: "Employee rejected",
    key: "rejectedByHuman" as const,
    borderColor: "#FBBC05",
    backgroundColor: "rgba(251, 188, 5, 0.15)",
  },
];

export const groupPickupTimeSeries = (
  series: PickupStatsTimeSeriesPoint[],
  mode: PickupTimeSeriesViewMode
): GroupedTimeSeriesPoint[] => {
  const grouped = new Map<string, GroupedTimeSeriesPoint>();

  series.forEach((point) => {
    const date = moment(getTimeSeriesDate(point));
    if (!date.isValid()) return;

    let label = "";
    let sortKey = date.valueOf();

    if (mode === "daily") {
      label = date.format("MMM D, YYYY");
      sortKey = date.clone().startOf("day").valueOf();
    } else if (mode === "weekly") {
      label = `Week ${date.isoWeek()} - ${date.year()}`;
      sortKey = date.clone().startOf("isoWeek").valueOf();
    } else {
      label = date.format("MMM YYYY");
      sortKey = date.clone().startOf("month").valueOf();
    }

    const existing = grouped.get(label);
    if (existing) {
      existing.received += point.received;
      existing.autoCreated += point.autoCreated;
      existing.approvedByHuman += point.approvedByHuman;
      existing.rejectedByHuman += point.rejectedByHuman;
      return;
    }

    grouped.set(label, {
      label,
      sortKey,
      received: point.received,
      autoCreated: point.autoCreated,
      approvedByHuman: point.approvedByHuman,
      rejectedByHuman: point.rejectedByHuman,
    });
  });

  return Array.from(grouped.values()).sort((a, b) => a.sortKey - b.sortKey);
};

export const buildPickupVolumeChartData = (
  series: PickupStatsTimeSeriesPoint[],
  mode: PickupTimeSeriesViewMode
) => {
  const grouped = groupPickupTimeSeries(series, mode);
  if (!grouped.length) return null;

  return {
    labels: grouped.map((point) => point.label),
    datasets: VOLUME_DATASET_STYLES.map((style) => ({
      label: style.label,
      data: grouped.map((point) => point[style.key]),
      borderColor: style.borderColor,
      backgroundColor: style.backgroundColor,
      tension: 0.3,
    })),
  };
};

export const getVolumeChartTitle = (mode: PickupTimeSeriesViewMode): string => {
  if (mode === "weekly") return "Weekly volume over time";
  if (mode === "monthly") return "Monthly volume over time";
  return "Daily volume over time";
};
